/**
 * batch-71 profiel-in-beeld (FO-538) - wat er op de slide komt en of het past.
 *
 * Puur en gedeeld. Dezelfde functies draaien in de fill-engine (de `.pptx`) en in de
 * browser (`ProfielSlide`, de live tellers in de editor), zodat PowerPoint, PDF, app
 * en klantportaal dezelfde regels, dezelfde verkleining en dezelfde "+ n meer" tonen.
 * Er is dus maar één plek die beslist hoe een werkervaring-kop eruitziet of wanneer
 * een lijst te lang wordt.
 *
 * Autofit (spec §1.3, §6.1):
 *  - tekstkolom: de letter en de regelafstand schalen mee tot `minPuntgrootte`; past
 *    het op de normale grootte niet, dan is dat een waarschuwing met het aantal regels
 *    te veel, ook als het verkleind nog wel past. Genereren blokkeert daar nooit op;
 *  - vinkjes: eerst compacter (regelafstand tot 22/24), daarna "+ n meer" binnen het
 *    vlak van het ontwerp. Wat niet past verdwijnt nooit stil;
 *  - losse velden (naam): de letter schaalt mee met de breedte tot de rand van het vlak.
 *
 * We schrijven de uitkomst als echte `sz`/`spcPts` in de runs, niet als `normAutofit`:
 * dan rekent PowerPoint noch LibreOffice er zelf nog iets anders van.
 *
 * Schatting: de maten in `profiel-metrics` zijn die van Tahoma (plus de vakjes uit Segoe UI
 * Symbol). Heeft een lettertype van de template geen eigen maten, of draait de generator op
 * de standaardtemplate, dan rekent alles hierboven gewoon door, maar is de uitkomst een
 * schatting. Dat wordt altijd gemeld (SCHATTING) en de tellers heten dan "geschat".
 */
import {
  PROFIEL_EMU_PER_PT,
  PROFIEL_FOTO_UITSNEDE_STANDAARD,
  PROFIEL_TOKENS,
  type ProfielCheckbox,
  type ProfielData,
  type ProfielFotoUitsnede,
  type ProfielLayout,
  type ProfielLayoutParagraaf,
  type ProfielLayoutRun,
  type ProfielLayoutSlide,
  type ProfielLayoutTekst,
  type ProfielLayoutVorm,
  type ProfielOverrides,
  type ProfielTemplateTerugval,
  type ProfielTokenDef,
  type ProfielWaarschuwing,
  type ProfielWerkervaringItem,
} from '@streep/types'
import { PROFIEL_REGELHOOGTE_EM, profielLettertypeGemeten, profielTekenBreedte } from './profiel-metrics'

// ── Regels ───────────────────────────────────────────────────────────────────

export const PROFIEL_GLYPH_AAN = '☒'
export const PROFIEL_GLYPH_UIT = '☐'

export type ProfielRegelSoort = 'kop' | 'bullet' | 'alinea' | 'witregel' | 'vinkje' | 'meer'

export interface ProfielRegelRun {
  tekst: string
  stijl: 'normaal' | 'vet' | 'glyph'
}

export interface ProfielRegel {
  soort: ProfielRegelSoort
  runs: ProfielRegelRun[]
  /** Vinkje: de checkbox-key (voor klikken in de editor). */
  sleutel?: string
  checked?: boolean
  /** Index in de bronlijst (werkervaring-blok, alinea, competentie). */
  bronIndex?: number
}

const WITREGEL: ProfielRegel = { soort: 'witregel', runs: [] }

/** Een em- of en-dash wordt in de gegenereerde kop een gewoon koppelteken (A1). */
function zonderLangeStreep(tekst: string): string {
  return tekst.replace(/\s*[—–]\s*/g, ' - ').trim()
}

/** `Functie - Bedrijf` (vet) + ` - 2024 - heden` (normaal), zoals het ontwerp (A1). */
export function profielWerkervaringKop(item: ProfielWerkervaringItem): { vet: string; normaal: string } {
  const bedrijf = item.bedrijf ? zonderLangeStreep(item.bedrijf) : ''
  const periode = item.periode ? zonderLangeStreep(item.periode) : ''
  const functie = zonderLangeStreep(item.functie)
  return {
    vet: bedrijf ? `${functie} - ${bedrijf}` : functie,
    normaal: periode ? ` - ${periode}` : '',
  }
}

export function profielWerkervaringRegels(items: ProfielWerkervaringItem[]): ProfielRegel[] {
  const regels: ProfielRegel[] = []
  items.forEach((item, index) => {
    if (index > 0) regels.push({ ...WITREGEL, bronIndex: index })
    const kop = profielWerkervaringKop(item)
    regels.push({
      soort: 'kop',
      bronIndex: index,
      runs: [
        { tekst: kop.vet, stijl: 'vet' },
        ...(kop.normaal ? [{ tekst: kop.normaal, stijl: 'normaal' as const }] : []),
      ],
    })
    for (const bullet of item.bullets) {
      regels.push({ soort: 'bullet', bronIndex: index, runs: [{ tekst: `- ${bullet}`, stijl: 'normaal' }] })
    }
  })
  return regels
}

/** Alinea's (split op lege regel) met een witregel ertussen. */
export function profielAlineaRegels(tekst: string | null): ProfielRegel[] {
  if (!tekst) return []
  const alineas = tekst
    .split(/\n\s*\n/)
    .map((a) => a.trim())
    .filter((a) => a.length > 0)
  const regels: ProfielRegel[] = []
  alineas.forEach((alinea, index) => {
    if (index > 0) regels.push({ ...WITREGEL, bronIndex: index })
    regels.push({ soort: 'alinea', bronIndex: index, runs: [{ tekst: alinea.replace(/\s*\n\s*/g, ' '), stijl: 'normaal' }] })
  })
  return regels
}

export function profielCompetentieRegels(items: string[]): ProfielRegel[] {
  return items.map((item, index) => ({
    soort: 'bullet' as const,
    bronIndex: index,
    runs: [{ tekst: `- ${item}`, stijl: 'normaal' as const }],
  }))
}

export function profielVinkjeRegel(item: ProfielCheckbox): ProfielRegel {
  return {
    soort: 'vinkje',
    sleutel: item.key,
    checked: item.checked,
    runs: [
      { tekst: `${item.checked ? PROFIEL_GLYPH_AAN : PROFIEL_GLYPH_UIT}  `, stijl: 'glyph' },
      { tekst: item.label, stijl: 'normaal' },
    ],
  }
}

function meerRegel(aantal: number): ProfielRegel {
  return { soort: 'meer', runs: [{ tekst: `+ ${aantal} meer`, stijl: 'normaal' }] }
}

function vinkjesVan(key: string, data: ProfielData): ProfielCheckbox[] | null {
  switch (key) {
    case 'opleidingen_verplicht_checked':
      return data.verplichteOpleidingen
    case 'opleidingen_aanvullend_checked':
      return data.aanvullendeOpleidingen
    case 'functies_checked':
      return data.functies
    case 'beschikbaarheid':
      return data.beschikbaarheid
    default:
      return null
  }
}

/** De regels van een lijst-token (geen vinkjes). */
export function profielTokenRegels(key: string, data: ProfielData): ProfielRegel[] {
  switch (key) {
    case 'werkervaring':
      return profielWerkervaringRegels(data.werkervaring)
    case 'persoonlijk_profiel':
      return profielAlineaRegels(data.klantProfielTekst)
    case 'profieltekst':
      return profielAlineaRegels(data.profielTekst)
    case 'kerncompetenties':
      return profielCompetentieRegels(data.kerncompetenties)
    default: {
      const vinkjes = vinkjesVan(key, data)
      return vinkjes ? vinkjes.map(profielVinkjeRegel) : []
    }
  }
}

/** De waarden van de inline-tokens. Een ontbrekend bedrijfsveld blijft leeg (§6.4). */
export function profielInlineWaarden(data: ProfielData): Record<string, string> {
  const b = data.bedrijf
  return {
    naam: data.naam,
    geboortejaar: data.geboortejaar ?? '',
    woonplaats: data.woonplaats ?? '',
    bedrijf_naam: b.naam,
    bedrijf_adres: b.adres ?? '',
    bedrijf_postcode_plaats: [b.postcode, b.plaats].filter(Boolean).join('  '),
    bedrijf_email: b.email ?? '',
    bedrijf_kvk: b.kvk ?? '',
    bedrijf_btw: b.btw ?? '',
    bedrijf_iban: b.iban ?? '',
  }
}

const INLINE_TOKEN_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g

/** Vervang inline-tokens in een stuk tekst (ook bij meerdere tokens in één run). */
export function profielVervangInline(tekst: string, waarden: Record<string, string>): string {
  return tekst.replace(INLINE_TOKEN_RE, (hele, naam: string) => waarden[naam] ?? hele)
}

// ── Token-registry-helpers ───────────────────────────────────────────────────

const TOKEN_PER_NAAM: ReadonlyMap<string, ProfielTokenDef> = new Map(
  PROFIEL_TOKENS.flatMap((def) => [
    [def.key, def] as [string, ProfielTokenDef],
    ...(def.aliassen ?? []).map((alias) => [alias, def] as [string, ProfielTokenDef]),
  ]),
)

export function profielTokenDef(naam: string): ProfielTokenDef | null {
  return TOKEN_PER_NAAM.get(naam) ?? null
}

// ── Overrides ("alleen voor dit document") ───────────────────────────────────

function pasVinkjesToe(items: ProfielCheckbox[], keuze: Record<string, boolean> | undefined): ProfielCheckbox[] {
  if (!keuze) return items
  return items.map((item) => (item.key in keuze ? { ...item, checked: keuze[item.key] === true } : item))
}

/**
 * Leg de document-overrides over de medewerkerdata. Opleidingsvinkjes zijn afgeleid
 * van de certificaten en kennen geen override; de bedrijfsgegevens ook niet.
 */
export function pasProfielOverridesToe(data: ProfielData, overrides: ProfielOverrides | null | undefined): ProfielData {
  if (!overrides) return data
  return {
    ...data,
    ...(overrides.naam !== undefined ? { naam: overrides.naam } : {}),
    ...(overrides.geboortejaar !== undefined ? { geboortejaar: overrides.geboortejaar } : {}),
    ...(overrides.woonplaats !== undefined ? { woonplaats: overrides.woonplaats } : {}),
    ...(overrides.persoonlijkProfiel !== undefined ? { klantProfielTekst: overrides.persoonlijkProfiel } : {}),
    ...(overrides.kerncompetenties !== undefined ? { kerncompetenties: overrides.kerncompetenties } : {}),
    ...(overrides.werkervaring !== undefined ? { werkervaring: overrides.werkervaring } : {}),
    functies: pasVinkjesToe(data.functies, overrides.functies),
    beschikbaarheid: pasVinkjesToe(data.beschikbaarheid, overrides.beschikbaarheid),
  }
}

// ── Meten ────────────────────────────────────────────────────────────────────

export function profielTekstBreedtePt(tekst: string, pt: number, vet = false, spatiePt = 0): number {
  let breedte = 0
  for (const teken of tekst) {
    breedte += profielTekenBreedte(teken.codePointAt(0) ?? 0x20, vet) * pt + spatiePt
  }
  return breedte
}

interface MeetRun {
  tekst: string
  pt: number
  vet: boolean
  spatiePt: number
}

/** Aantal regels van een alinea bij deze breedte: woorden lopen door over de runs. */
function telRegels(runs: MeetRun[], breedtePt: number): number {
  if (breedtePt <= 0) return 1
  let regels = 1
  let x = 0
  for (const run of runs) {
    for (const deel of run.tekst.split(/(\s+)/)) {
      if (deel.length === 0) continue
      const w = profielTekstBreedtePt(deel, run.pt, run.vet, run.spatiePt)
      if (/^\s+$/.test(deel)) {
        x += w
        continue
      }
      if (x + w > breedtePt && x > 0) {
        regels += 1
        x = 0
      }
      // Een woord breder dan de kolom breekt midden in het woord af.
      let rest = w
      while (rest > breedtePt) {
        regels += 1
        rest -= breedtePt
      }
      x += rest
    }
  }
  return regels
}

function ankerRun(paragraaf: ProfielLayoutParagraaf): ProfielLayoutRun | null {
  return paragraaf.runs[paragraaf.runs.length - 1] ?? null
}

function glyphRun(paragraaf: ProfielLayoutParagraaf): ProfielLayoutRun | null {
  return paragraaf.runs.length > 1 ? (paragraaf.runs[0] ?? null) : null
}

function regelafstand(paragraaf: ProfielLayoutParagraaf, pt: number): number {
  if (paragraaf.regelafstandPt !== null) return paragraaf.regelafstandPt
  const basis = pt * PROFIEL_REGELHOOGTE_EM
  return paragraaf.regelafstandPct !== null ? (basis * paragraaf.regelafstandPct) / 100 : basis
}

/** Ondergrens van de schaal voor een token bij deze basisgrootte. */
function minSchaal(key: string, pt: number): number {
  const min = profielTokenDef(key)?.minPuntgrootte
  if (!min || pt <= min) return 0.78
  return min / pt
}

function emuNaarPt(emu: number): number {
  return emu / PROFIEL_EMU_PER_PT
}

// ── Fit ──────────────────────────────────────────────────────────────────────

/** Minimale regelafstand van de vinkjes t.o.v. het ontwerp (22 pt op 24 pt). */
export const PROFIEL_VINKJES_MIN_REGELAFSTAND = 22 / 24

export interface ProfielVormFit {
  slide: number
  vormId: number
  /** Schaal van de letter van de token-inhoud (1 = ontwerp). */
  tokenSchaal: number
  /** Schaal van de regelafstand van de token-inhoud. */
  regelSchaal: number
  /** Schaal van de regelafstand van de vaste tekst in dezelfde vorm (koppen, spacers). */
  vasteRegelSchaal: number
  /** De regels per token-anker, inclusief eventuele "+ n meer". */
  regels: Record<string, ProfielRegel[]>
  /** Paragraaf-index -> schaal voor runs met een inline-token (naam op de cover). */
  paragraafSchaal: Record<number, number>
  /** Past ook op de ondergrens niet op één regel: laat teruglopen binnen deze breedte. */
  terugloopBreedteEmu: number | null
  /**
   * Een vorm zonder terugloop waarvan de tekst breder is dan het vlak in de template:
   * de vorm groeit mee (PowerPoint doet dat zelf, LibreOffice breekt anders af op de
   * oude breedte). Null = de breedte uit de template klopt.
   */
  breedteEmu: number | null
}

export interface ProfielFitTeller {
  sleutel: string
  slide: number
  tokens: string[]
  label: string
  /** Regels bij de normale grootte. */
  regels: number
  /** Regels die op de normale grootte passen. */
  maxRegels: number
  regelsTeVeel: number
  /** Puntgrootte na autofit. */
  puntgrootte: number
  /** Past ook verkleind niet meer. */
  pastNiet: boolean
  verborgen: number
  /** Gerekend zonder de echte maten (zie {@link ProfielFit.schatting}): toon de teller als geschat. */
  geschat: boolean
}

/** Een teller zoals de fit-stappen hem maken; `geschat` komt er aan het eind bij. */
type FitTeller = Omit<ProfielFitTeller, 'geschat'>

export interface ProfielFit {
  vormen: Record<string, ProfielVormFit>
  tellers: ProfielFitTeller[]
  waarschuwingen: ProfielWaarschuwing[]
  /** Waarom deze passing een schatting is, of null als hij met de echte maten gerekend is. */
  schatting: ProfielSchatting | null
}

export interface ProfielFitOpties {
  /**
   * Waarom de generator op de standaardtemplate draait in plaats van de actieve template.
   * Null of weggelaten: de actieve template (of een bewust gekozen versie).
   */
  templateTerugval?: ProfielTemplateTerugval | null
}

export function profielVormSleutel(slide: number, vormId: number): string {
  return `${slide}:${vormId}`
}

// ── Schatting: rekent de fit met de echte maten? ────────────────────────────

export interface ProfielSchatting {
  /** Lettertypen van de template zonder eigen maten in profiel-metrics (gerekend als Tahoma). */
  ongemetenLettertypen: string[]
  /** De generator draait op de standaardtemplate; null = op de actieve template. */
  templateTerugval: ProfielTemplateTerugval | null
}

/**
 * De lettertypen in deze layout waarvan profiel-metrics de tekenbreedtes niet kent: elk
 * lettertype uit `layout.lettertypen` zonder breedtetabel, plus een lettertype met een
 * gedeeltelijke tabel (Segoe UI Symbol: alleen de vakjes) dat voor andere tekst gebruikt wordt.
 */
export function profielOngemetenLettertypen(layout: ProfielLayout): string[] {
  // Kleine letters als sleutel, de naam zoals de template hem schrijft als waarde.
  const ongemeten = new Map<string, string>()
  const voegToe = (naam: string) => {
    const sleutel = naam.trim().toLowerCase()
    if (sleutel && !ongemeten.has(sleutel)) ongemeten.set(sleutel, naam.trim())
  }
  for (const naam of layout.lettertypen) {
    if (!profielLettertypeGemeten(naam)) voegToe(naam)
  }
  for (const slide of layout.slides) {
    for (const vorm of tekstVormen(slide)) {
      for (const paragraaf of vorm.paragrafen) {
        for (const run of paragraaf.runs) {
          if (!run.lettertype || run.tekst.trim().length === 0) continue
          if (!profielLettertypeGemeten(run.lettertype, run.tekst)) voegToe(run.lettertype)
        }
      }
    }
  }
  return [...ongemeten.values()].sort((a, b) => a.localeCompare(b, 'nl'))
}

/**
 * Is de passing een schatting? Ja als een lettertype van de template geen maten heeft, of als
 * de generator op de standaardtemplate draait. Zonder layout telt alleen de standaardtemplate.
 */
export function bepaalProfielSchatting(
  layout: ProfielLayout | null,
  templateTerugval?: ProfielTemplateTerugval | null,
): ProfielSchatting | null {
  const ongemetenLettertypen = layout ? profielOngemetenLettertypen(layout) : []
  const terugval = templateTerugval ?? null
  if (ongemetenLettertypen.length === 0 && terugval === null) return null
  return { ongemetenLettertypen, templateTerugval: terugval }
}

/** "Calibri", "Arial en Calibri", "Arial, Calibri en Verdana". */
function opsomming(namen: string[]): string {
  if (namen.length <= 1) return namen[0] ?? ''
  return `${namen.slice(0, -1).join(', ')} en ${namen[namen.length - 1]}`
}

/** De melding in gewone taal: dat het een schatting is, waarom, en wat je ermee doet. */
export function profielSchattingBericht(schatting: ProfielSchatting): string {
  const zinnen = ['Of de tekst op de slides past, is een schatting.']
  const terugval = schatting.templateTerugval
  if (terugval?.reden === 'NIET_TE_LADEN') {
    const versie = terugval.actieveVersie !== null ? ` (versie ${terugval.actieveVersie})` : ''
    zinnen.push(
      `De actieve profieltemplate${versie} kon niet worden geladen, daarom is de standaardtemplate (${terugval.bestandsnaam}) gebruikt in plaats van het ontwerp.`,
    )
  } else if (terugval) {
    zinnen.push(
      `Er is geen actieve profieltemplate, daarom is de standaardtemplate (${terugval.bestandsnaam}) gebruikt in plaats van het ontwerp.`,
    )
  }
  const namen = schatting.ongemetenLettertypen
  if (namen.length > 0) {
    zinnen.push(
      `Van ${namen.length === 1 ? 'het lettertype' : 'de lettertypen'} ${opsomming(namen)} kent de app de tekenbreedtes niet; er is gerekend met die van Tahoma.`,
    )
  }
  zinnen.push('Controleer het document zelf voordat je het verstuurt.')
  if (terugval?.reden === 'NIET_TE_LADEN') zinnen.push('Meld dit bij beheer (Instellingen > Document Templates).')
  else if (terugval) zinnen.push('Beheer kan het ontwerp actief zetten onder Instellingen > Document Templates.')
  return zinnen.join(' ')
}

/** De SCHATTING-waarschuwing: geldt voor het hele document, dus zonder slide. */
export function profielSchattingWaarschuwing(schatting: ProfielSchatting): ProfielWaarschuwing {
  return { soort: 'SCHATTING', token: 'layout', slide: null, bericht: profielSchattingBericht(schatting) }
}

/** Rood in de editor: er staat iets niet (helemaal) op de slide. De rest is amber. */
export function profielWaarschuwingIsRood(waarschuwing: Pick<ProfielWaarschuwing, 'soort'>): boolean {
  return waarschuwing.soort === 'TE_LANG' || waarschuwing.soort === 'AFGEKAPT'
}

function tekstVormen(slide: ProfielLayoutSlide): ProfielLayoutTekst[] {
  return slide.vormen.filter((v): v is ProfielLayoutTekst => v.soort === 'tekst')
}

/**
 * Tot waar mag een regel zonder terugloop doorlopen? Tot het eerstvolgende tekstvak
 * ernaast, of anders tot de rechterrand van de foto waar hij op staat, of anders de
 * slide. Generiek, zodat een geüploade template dezelfde bescherming krijgt.
 */
function rechterGrensEmu(vorm: ProfielLayoutTekst, slide: ProfielLayoutSlide, layout: ProfielLayout): number {
  const marge = 150_000 // 12 pt lucht
  let grens = layout.breedteEmu - marge
  for (const ander of slide.vormen) {
    if (ander === vorm) continue
    if (ander.soort === 'tekst') {
      const overlaptVerticaal = ander.y < vorm.y + vorm.cy && ander.y + ander.cy > vorm.y
      if (overlaptVerticaal && ander.x > vorm.x) grens = Math.min(grens, ander.x - marge)
    } else if (ander.soort === 'foto' && !ander.isProfielFoto) {
      // Het portret zelf telt niet: dat is schuin afgesneden en overlapt het vlak eronder.
      const bevat =
        ander.x <= vorm.x && ander.x + ander.cx > vorm.x && ander.y <= vorm.y && ander.y + ander.cy > vorm.y
      if (bevat) grens = Math.min(grens, ander.x + ander.cx - marge)
    }
  }
  return grens
}

/** De onderkant van de laagste foto: daar lijnt een tekstkolom op uit. */
function onderGrensEmu(vorm: ProfielLayoutTekst, slide: ProfielLayoutSlide): number {
  let grens = vorm.y + vorm.cy
  for (const ander of slide.vormen) {
    if (ander.soort === 'foto') grens = Math.max(grens, ander.y + ander.cy)
  }
  return grens
}

function vulInline(run: ProfielLayoutRun, waarden: Record<string, string>): string {
  return profielVervangInline(run.tekst, waarden)
}

function fitInlineParagrafen(
  vorm: ProfielLayoutTekst,
  slide: ProfielLayoutSlide,
  layout: ProfielLayout,
  waarden: Record<string, string>,
  fit: ProfielVormFit,
  waarschuwingen: ProfielWaarschuwing[],
): void {
  if (vorm.terugloop) return
  const budget = emuNaarPt(rechterGrensEmu(vorm, slide, layout) - vorm.x)
  vorm.paragrafen.forEach((paragraaf, index) => {
    if (paragraaf.token) return
    const inline = paragraaf.runs.flatMap((r) =>
      [...r.tekst.matchAll(INLINE_TOKEN_RE)].map((m) => m[1] ?? ''),
    )
    if (inline.length === 0) return
    const breedte =
      paragraaf.runs.reduce(
        (som, r) => som + profielTekstBreedtePt(vulInline(r, waarden), r.pt, r.vet, r.spatiePt),
        0,
      ) + paragraaf.margeLinksPt
    const groei = (pt: number) => {
      const emu = Math.ceil((pt + 2) * PROFIEL_EMU_PER_PT)
      if (emu > vorm.cx) fit.breedteEmu = Math.max(fit.breedteEmu ?? 0, emu)
    }
    if (breedte <= budget) {
      groei(breedte)
      return
    }
    const eerste = inline[0] ?? ''
    const pt = paragraaf.runs[0]?.pt ?? 18
    const schaal = Math.max(budget / breedte, minSchaal(eerste, pt))
    fit.paragraafSchaal[index] = schaal
    const label = profielTokenDef(eerste)?.label ?? eerste
    groei(Math.min(breedte * schaal, budget))
    if (breedte * schaal > budget) {
      fit.terugloopBreedteEmu = Math.round(budget * PROFIEL_EMU_PER_PT)
      waarschuwingen.push({
        soort: 'TE_LANG',
        token: eerste,
        slide: slide.nummer,
        bericht: `${label} past ook op ${Math.round(pt * schaal)} pt niet op één regel op slide ${slide.nummer}: de tekst loopt terug.`,
        puntgrootte: Math.round(pt * schaal),
      })
    } else {
      waarschuwingen.push({
        soort: 'VERKLEIND',
        token: eerste,
        slide: slide.nummer,
        bericht: `${label} is op slide ${slide.nummer} verkleind naar ${Math.round(pt * schaal)} pt om binnen het vlak te blijven.`,
        puntgrootte: Math.round(pt * schaal),
      })
    }
  })
}

/** Kies welke vinkjes zichtbaar blijven: aangevinkt gaat voor, de volgorde blijft. */
function kiesZichtbareVinkjes(items: ProfielCheckbox[], aantal: number): ProfielCheckbox[] {
  if (items.length <= aantal) return items
  const aangevinkt = items.filter((i) => i.checked)
  const leeg = items.filter((i) => !i.checked)
  const gekozen = new Set([...aangevinkt, ...leeg].slice(0, aantal))
  return items.filter((i) => gekozen.has(i))
}

function fitVinkjes(
  vorm: ProfielLayoutTekst,
  slide: ProfielLayoutSlide,
  layout: ProfielLayout,
  data: ProfielData,
  fit: ProfielVormFit,
  tellers: FitTeller[],
  waarschuwingen: ProfielWaarschuwing[],
): void {
  const anker = vorm.paragrafen.filter((p) => p.token && vinkjesVan(p.token, data) !== null)
  const lijsten = anker.map((paragraaf) => ({
    key: paragraaf.token as string,
    items: vinkjesVan(paragraaf.token as string, data) ?? [],
  }))
  const eersteAnker = anker[0]
  const eersteRun = eersteAnker ? ankerRun(eersteAnker) : null
  const basisPt = eersteRun?.pt ?? 18
  const basis = eersteAnker ? regelafstand(eersteAnker, basisPt) : basisPt * PROFIEL_REGELHOOGTE_EM
  const hoogte = emuNaarPt(vorm.cy) - vorm.marges.t - vorm.marges.b
  // De koppen en de lege regel tussen de lijsten schalen mee met de regelafstand.
  const vasteHoogte = vorm.paragrafen
    .filter((p) => p.token === null)
    .reduce((som, p) => {
      const pt = Math.max(...p.runs.map((r) => r.pt), p.eindePt ?? 0, 1)
      return som + regelafstand(p, pt)
    }, 0)
  const regelsNodig = lijsten.reduce((som, l) => som + Math.max(1, l.items.length), 0)
  const totaal = vasteHoogte + regelsNodig * basis

  const zichtbaar = new Map(lijsten.map((l) => [l.key, l.items]))
  /** Aangevinkte vinkjes die niet passen: die staan als "+ n meer" in het document. */
  const verborgenPerLijst = new Map<string, number>()
  /** Lege vakjes die zijn weggelaten: er mist geen informatie, dus geen "+ n meer". */
  const leegWegPerLijst = new Map<string, number>()

  // 1 pt speling: het ontwerp vult zijn eigen vlak op de punt nauwkeurig.
  if (totaal > hoogte + 1) {
    const schaal = hoogte / totaal
    if (schaal >= PROFIEL_VINKJES_MIN_REGELAFSTAND) {
      fit.regelSchaal = schaal
      fit.vasteRegelSchaal = schaal
    } else {
      fit.regelSchaal = PROFIEL_VINKJES_MIN_REGELAFSTAND
      fit.vasteRegelSchaal = PROFIEL_VINKJES_MIN_REGELAFSTAND
      const beschikbaar = Math.floor((hoogte / PROFIEL_VINKJES_MIN_REGELAFSTAND - vasteHoogte) / basis)
      // Elke lijst krijgt eerst de regels uit het ontwerp; wat overblijft gaat naar de
      // lijsten die meer nodig hebben.
      const regelsPerLijst = new Map(
        lijsten.map((l) => {
          const nominaal = profielTokenDef(l.key)?.maxRegelsOpSlide ?? l.items.length
          return [l.key, Math.max(1, Math.min(l.items.length, nominaal))]
        }),
      )
      const som = () => [...regelsPerLijst.values()].reduce((a, b) => a + b, 0)
      let over = beschikbaar - som()
      while (over > 0) {
        const kandidaat = lijsten.find((l) => (regelsPerLijst.get(l.key) ?? 0) < l.items.length)
        if (!kandidaat) break
        regelsPerLijst.set(kandidaat.key, (regelsPerLijst.get(kandidaat.key) ?? 0) + 1)
        over -= 1
      }
      while (over < 0) {
        const grootste = [...regelsPerLijst.entries()].sort((a, b) => b[1] - a[1])[0]
        if (!grootste || grootste[1] <= 1) break
        regelsPerLijst.set(grootste[0], grootste[1] - 1)
        over += 1
      }
      for (const lijst of lijsten) {
        const regels = regelsPerLijst.get(lijst.key) ?? lijst.items.length
        if (regels >= lijst.items.length) continue
        const aangevinkt = lijst.items.filter((i) => i.checked).length
        if (aangevinkt <= regels) {
          // Alleen lege vakjes vallen weg: alles wat aan staat blijft zichtbaar.
          const items = kiesZichtbareVinkjes(lijst.items, regels)
          zichtbaar.set(lijst.key, items)
          leegWegPerLijst.set(lijst.key, lijst.items.length - items.length)
        } else {
          // "+ n meer" telt alleen wat aan staat: een klant leest het als "nog n functies".
          const items = kiesZichtbareVinkjes(lijst.items, Math.max(0, regels - 1))
          zichtbaar.set(lijst.key, items)
          verborgenPerLijst.set(lijst.key, aangevinkt - items.length)
          leegWegPerLijst.set(lijst.key, lijst.items.length - aangevinkt)
        }
      }
    }
  }

  // Breedte: een te lang label mag niet in het volgende blok lopen.
  const budget = emuNaarPt(rechterGrensEmu(vorm, slide, layout) - vorm.x)
  let breedste = 0
  for (const paragraaf of anker) {
    const label = ankerRun(paragraaf)
    const glyph = glyphRun(paragraaf)
    const items = zichtbaar.get(paragraaf.token as string) ?? []
    for (const item of items) {
      const b =
        profielTekstBreedtePt(
          `${PROFIEL_GLYPH_AAN}  `,
          glyph?.pt ?? label?.pt ?? basisPt,
        ) + profielTekstBreedtePt(item.label, label?.pt ?? basisPt, false, label?.spatiePt ?? 0)
      breedste = Math.max(breedste, b + paragraaf.margeLinksPt)
    }
  }
  if (!vorm.terugloop && breedste > budget) {
    const key = lijsten[0]?.key ?? ''
    fit.tokenSchaal = Math.max(budget / breedste, minSchaal(key, basisPt))
    const passend = breedste * fit.tokenSchaal <= budget
    waarschuwingen.push({
      soort: passend ? 'VERKLEIND' : 'TE_LANG',
      token: key,
      slide: slide.nummer,
      bericht: passend
        ? `Een vinkje op slide ${slide.nummer} is te breed: de lijst is verkleind naar ${Math.round(basisPt * fit.tokenSchaal)} pt.`
        : `Een vinkje op slide ${slide.nummer} is ook op ${Math.round(basisPt * fit.tokenSchaal)} pt te breed en loopt door in het volgende blok.`,
      puntgrootte: Math.round(basisPt * fit.tokenSchaal),
    })
  }

  if (!vorm.terugloop) {
    const emu = Math.ceil((Math.min(breedste * fit.tokenSchaal, budget) + 2) * PROFIEL_EMU_PER_PT)
    if (emu > vorm.cx) fit.breedteEmu = Math.max(fit.breedteEmu ?? 0, emu)
  }

  for (const lijst of lijsten) {
    const items = zichtbaar.get(lijst.key) ?? lijst.items
    const verborgen = verborgenPerLijst.get(lijst.key) ?? 0
    const leegWeg = leegWegPerLijst.get(lijst.key) ?? 0
    fit.regels[lijst.key] = [
      ...items.map(profielVinkjeRegel),
      ...(verborgen > 0 ? [meerRegel(verborgen)] : []),
    ]
    const def = profielTokenDef(lijst.key)
    const maxNominaal = def?.maxRegelsOpSlide ?? lijst.items.length
    const label = def?.label ?? lijst.key
    tellers.push({
      sleutel: `${profielVormSleutel(slide.nummer, vorm.id)}:${lijst.key}`,
      slide: slide.nummer,
      tokens: [lijst.key],
      label,
      // Wat er na het compacter maken staat: past alles, dan is er niets "te veel".
      regels: items.length,
      maxRegels: Math.max(maxNominaal, items.length),
      regelsTeVeel: verborgen,
      puntgrootte: Math.round(basisPt * fit.tokenSchaal),
      pastNiet: verborgen > 0,
      verborgen,
    })
    if (verborgen > 0) {
      waarschuwingen.push({
        soort: 'AFGEKAPT',
        token: lijst.key,
        slide: slide.nummer,
        verborgen,
        bericht: `${label}: ${verborgen} aangevinkte vinkjes passen niet op slide ${slide.nummer} en staan als "+ ${verborgen} meer" in het document.${leegWeg > 0 ? ` ${leegWeg} lege vakjes zijn weggelaten.` : ''}`,
      })
    } else if (leegWeg > 0) {
      waarschuwingen.push({
        soort: 'COMPACT',
        token: lijst.key,
        slide: slide.nummer,
        bericht: `${label}: ${leegWeg} lege vakjes zijn weggelaten om op slide ${slide.nummer} te passen. Alles wat aan staat, staat erop.`,
      })
    }
  }
  if (fit.regelSchaal < 1 && verborgenPerLijst.size === 0 && leegWegPerLijst.size === 0) {
    const labels = lijsten
      .filter((l) => l.items.length > (profielTokenDef(l.key)?.maxRegelsOpSlide ?? l.items.length))
      .map((l) => profielTokenDef(l.key)?.label ?? l.key)
    waarschuwingen.push({
      soort: 'COMPACT',
      token: lijsten[0]?.key ?? '',
      slide: slide.nummer,
      bericht: `${labels.join(', ') || 'De vinkjes'}: meer regels dan het ontwerp, de regelafstand op slide ${slide.nummer} is compacter gemaakt.`,
    })
  }
}

/** Hoogte van de vorm bij schaal `s` (token-inhoud schaalt, vaste tekst niet). */
function kolomHoogte(vorm: ProfielLayoutTekst, data: ProfielData, s: number, breedte: number): number {
  let hoogte = 0
  for (const paragraaf of vorm.paragrafen) {
    hoogte += paragraaf.voorPt + paragraaf.naPt
    if (paragraaf.token) {
      const run = ankerRun(paragraaf)
      const pt = (run?.pt ?? paragraaf.eindePt ?? 18) * s
      const afstand = regelafstand(paragraaf, run?.pt ?? 18) * s
      for (const regel of profielTokenRegels(paragraaf.token, data)) {
        if (regel.soort === 'witregel') {
          hoogte += afstand / 2
          continue
        }
        const runs: MeetRun[] = regel.runs.map((r) => ({
          tekst: r.tekst,
          pt,
          vet: r.stijl === 'vet',
          spatiePt: run?.spatiePt ?? 0,
        }))
        hoogte += telRegels(runs, breedte - paragraaf.margeLinksPt) * afstand
      }
      continue
    }
    const pt = Math.max(...paragraaf.runs.map((r) => r.pt), paragraaf.eindePt ?? 0, 1)
    const runs: MeetRun[] = paragraaf.runs.map((r) => ({
      tekst: r.tekst,
      pt: r.pt,
      vet: r.vet,
      spatiePt: r.spatiePt,
    }))
    hoogte += (runs.length > 0 ? telRegels(runs, breedte - paragraaf.margeLinksPt) : 1) * regelafstand(paragraaf, pt)
  }
  return hoogte
}

function fitKolom(
  vorm: ProfielLayoutTekst,
  slide: ProfielLayoutSlide,
  data: ProfielData,
  fit: ProfielVormFit,
  tellers: FitTeller[],
  waarschuwingen: ProfielWaarschuwing[],
): void {
  const anker = vorm.paragrafen.filter((p) => p.token !== null)
  for (const paragraaf of anker) {
    fit.regels[paragraaf.token as string] = profielTokenRegels(paragraaf.token as string, data)
  }
  const breedte = emuNaarPt(vorm.cx) - vorm.marges.l - vorm.marges.r
  const beschikbaar = emuNaarPt(onderGrensEmu(vorm, slide) - vorm.y) - vorm.marges.t - vorm.marges.b
  const eerste = anker[0]
  const eersteRun = eerste ? ankerRun(eerste) : null
  const basisPt = eersteRun?.pt ?? 20
  const afstand = eerste ? regelafstand(eerste, basisPt) : basisPt * PROFIEL_REGELHOOGTE_EM
  const nominaal = kolomHoogte(vorm, data, 1, breedte)
  const leeg = kolomHoogte(vorm, { ...data, werkervaring: [], klantProfielTekst: null, profielTekst: null, kerncompetenties: [] }, 1, breedte)
  const regels = Math.round((nominaal - leeg) / afstand)
  const maxRegels = Math.max(0, Math.floor((beschikbaar - leeg) / afstand))
  const regelsTeVeel = nominaal > beschikbaar ? Math.ceil((nominaal - beschikbaar) / afstand) : 0

  const ondergrens = Math.max(...anker.map((p) => minSchaal(p.token as string, ankerRun(p)?.pt ?? basisPt)))
  let schaal = 1
  if (nominaal > beschikbaar) {
    schaal = ondergrens
    for (let s = 1; s >= ondergrens; s -= 0.01) {
      if (kolomHoogte(vorm, data, s, breedte) <= beschikbaar) {
        schaal = s
        break
      }
    }
  }
  fit.tokenSchaal = schaal
  fit.regelSchaal = schaal
  const pastNiet = kolomHoogte(vorm, data, schaal, breedte) > beschikbaar
  const labels = anker.map((p) => profielTokenDef(p.token as string)?.label ?? (p.token as string))
  const label = labels.join(' en ')
  const puntgrootte = Math.round(basisPt * schaal)
  tellers.push({
    sleutel: profielVormSleutel(slide.nummer, vorm.id),
    slide: slide.nummer,
    tokens: anker.map((p) => p.token as string),
    label,
    regels,
    maxRegels,
    regelsTeVeel,
    puntgrootte,
    pastNiet,
    verborgen: 0,
  })
  if (regelsTeVeel > 0) {
    waarschuwingen.push({
      soort: 'TE_LANG',
      token: anker[0]?.token ?? '',
      slide: slide.nummer,
      regelsTeVeel,
      puntgrootte,
      bericht: pastNiet
        ? `${label}: te lang voor slide ${slide.nummer}, ${regelsTeVeel} regels te veel. Ook op de ondergrens van ${puntgrootte} pt past het niet.`
        : `${label}: te lang voor slide ${slide.nummer}, ${regelsTeVeel} regels te veel. De tekst is verkleind naar ${puntgrootte} pt.`,
    })
  }
}

/**
 * Reken de hele presentatie door. Dezelfde uitkomst in de editor, de generator en de
 * klant-weergave. Waarschuwingen staan per token één keer in de lijst: de linkerkolom
 * staat op twee slides, maar dat is één melding.
 *
 * Is de passing een schatting (een lettertype zonder maten, of `opties.templateTerugval`),
 * dan staat de SCHATTING-melding vooraan en zijn alle tellers `geschat`. De meting zelf is
 * dezelfde.
 */
export function berekenProfielFit(layout: ProfielLayout, data: ProfielData, opties: ProfielFitOpties = {}): ProfielFit {
  const waarden = profielInlineWaarden(data)
  const vormen: Record<string, ProfielVormFit> = {}
  const tellers: FitTeller[] = []
  const waarschuwingen: ProfielWaarschuwing[] = []
  for (const slide of layout.slides) {
    for (const vorm of tekstVormen(slide)) {
      if (vorm.tokens.length === 0) continue
      const fit: ProfielVormFit = {
        slide: slide.nummer,
        vormId: vorm.id,
        tokenSchaal: 1,
        regelSchaal: 1,
        vasteRegelSchaal: 1,
        regels: {},
        paragraafSchaal: {},
        terugloopBreedteEmu: null,
        breedteEmu: null,
      }
      const anker = vorm.paragrafen.filter((p) => p.token !== null)
      const heeftVinkjes = anker.some((p) => vinkjesVan(p.token as string, data) !== null)
      if (heeftVinkjes) {
        fitVinkjes(vorm, slide, layout, data, fit, tellers, waarschuwingen)
      }
      // Een template mag lijst-tokens en vinkjes in één vorm zetten; de kolom-fit
      // rekent dan alleen de lijst-tokens (de vinkjes zijn hierboven al gedaan).
      if (anker.some((p) => vinkjesVan(p.token as string, data) === null)) {
        const alleenLijst: ProfielLayoutTekst = {
          ...vorm,
          paragrafen: vorm.paragrafen.filter(
            (p) => p.token === null || vinkjesVan(p.token, data) === null,
          ),
        }
        fitKolom(alleenLijst, slide, data, fit, tellers, waarschuwingen)
      }
      fitInlineParagrafen(vorm, slide, layout, waarden, fit, waarschuwingen)
      vormen[profielVormSleutel(slide.nummer, vorm.id)] = fit
    }
  }
  const schatting = bepaalProfielSchatting(layout, opties.templateTerugval)
  return {
    vormen,
    tellers: uniekeTellers(tellers).map((teller) => ({ ...teller, geschat: schatting !== null })),
    waarschuwingen: [
      ...(schatting ? [profielSchattingWaarschuwing(schatting)] : []),
      ...uniekeWaarschuwingen(waarschuwingen),
    ],
    schatting,
  }
}

function uniekeTellers(tellers: FitTeller[]): FitTeller[] {
  const gezien = new Set<string>()
  return tellers.filter((t) => {
    const sleutel = t.tokens.join('+')
    if (gezien.has(sleutel)) return false
    gezien.add(sleutel)
    return true
  })
}

function uniekeWaarschuwingen(lijst: ProfielWaarschuwing[]): ProfielWaarschuwing[] {
  const gezien = new Set<string>()
  return lijst.filter((w) => {
    const sleutel = `${w.soort}:${w.token}`
    if (gezien.has(sleutel)) return false
    gezien.add(sleutel)
    return true
  })
}

/** Waarschuwingen die niet uit de maatvoering komen: geen foto, lege bedrijfsvelden (§6.2, §6.4). */
export function profielDataWaarschuwingen(data: ProfielData): ProfielWaarschuwing[] {
  const uit: ProfielWaarschuwing[] = []
  if (!data.heeftFoto) {
    uit.push({
      soort: 'GEEN_FOTO',
      token: 'profiel-foto',
      slide: null,
      bericht: 'Er is geen portretfoto: het document toont het lege kader.',
    })
  }
  const b = data.bedrijf
  const leeg = [
    ['KVK', b.kvk],
    ['btw-nummer', b.btw],
    ['IBAN', b.iban],
    ['adres', b.adres],
    ['e-mail', b.email],
  ]
    .filter(([, waarde]) => !waarde)
    .map(([naam]) => naam)
  if (leeg.length > 0) {
    uit.push({
      soort: 'LEEG_VELD',
      token: 'bedrijf',
      slide: null,
      bericht: `De bedrijfsgegevens zijn niet compleet: ${leeg.join(', ')} ontbreekt bij de BV. De slide toont die velden leeg.`,
    })
  }
  return uit
}

/** Alle waarschuwingen samen, zoals de versie ze opslaat en de editor ze toont. */
export function profielWaarschuwingen(
  layout: ProfielLayout,
  data: ProfielData,
  opties: ProfielFitOpties = {},
): ProfielWaarschuwing[] {
  return [...berekenProfielFit(layout, data, opties).waarschuwingen, ...profielDataWaarschuwingen(data)]
}

// ── Bulk-ZIP: wat er niet paste, als tekstbestand ────────────────────────────

export interface ProfielBulkWaarschuwingen {
  medewerker: string
  /** De bestandsnaam in de ZIP, zodat het profiel terug te vinden is. */
  bestand: string
  waarschuwingen: ProfielWaarschuwing[]
}

/**
 * De inhoud van `waarschuwingen.txt` in de bulk-ZIP van de profielschetsen. Bovenaan één keer
 * de schatting (die geldt voor de hele export: één template), daarna per medewerker wat er
 * niet paste (de rode meldingen van de editor). Null = niets te melden: dan geen bestand.
 *
 * Niets hiervan houdt een bestand tegen: elk profiel staat in de ZIP (§6.1).
 */
export function profielBulkWaarschuwingenTekst(profielen: ProfielBulkWaarschuwingen[]): string | null {
  const schattingen = [
    ...new Set(profielen.flatMap((p) => p.waarschuwingen.filter((w) => w.soort === 'SCHATTING').map((w) => w.bericht))),
  ]
  const nietPassend = profielen
    .map((p) => ({ ...p, meldingen: p.waarschuwingen.filter(profielWaarschuwingIsRood) }))
    .filter((p) => p.meldingen.length > 0)
  if (schattingen.length === 0 && nietPassend.length === 0) return null

  const regels = [
    'Profielschetsen: waarschuwingen bij het genereren',
    '',
    'Alle profielen staan in deze ZIP. Wat hieronder staat, past niet (helemaal) op de slides of is geschat: controleer die profielen voordat je ze verstuurt.',
  ]
  if (schattingen.length > 0) {
    regels.push('', 'Voor alle profielen in deze export:')
    for (const bericht of schattingen) regels.push(`- ${bericht}`)
  }
  if (nietPassend.length > 0) {
    regels.push('', 'Wat niet op de slides past:')
    for (const profiel of nietPassend) {
      regels.push('', `${profiel.medewerker} (${profiel.bestand})`)
      for (const melding of profiel.meldingen) regels.push(`- ${melding.bericht}`)
    }
  }
  return `${regels.join('\n')}\n`
}

// ── Schermweergave: de opgeloste paragrafen van een tekstvorm ────────────────

export interface ProfielWeergaveRun {
  tekst: string
  pt: number
  vet: boolean
  cursief: boolean
  kleur: string | null
  lettertype: string | null
  spatiePt: number
}

export interface ProfielWeergaveParagraaf {
  sleutel: string
  /** Token waar deze regel uit voortkomt (null = vaste tekst). */
  token: string | null
  /** Het inline-token in deze regel (`naam`, `bedrijf_kvk`), of null. */
  inlineToken: string | null
  regel: ProfielRegel | null
  runs: ProfielWeergaveRun[]
  regelafstandPt: number
  voorPt: number
  naPt: number
  margeLinksPt: number
  inspringPt: number
  uitlijning: ProfielLayoutParagraaf['uitlijning']
  /** Hoogte van een lege paragraaf. */
  leegPt: number
}

/**
 * De paragrafen van een tekstvorm zoals ze op de slide staan, met tokens ingevuld en
 * de autofit toegepast. Dit is de HTML-tegenhanger van wat `fillProfielTemplate` in de
 * slide-XML schrijft.
 */
export function profielWeergaveParagrafen(
  vorm: ProfielLayoutTekst,
  fit: ProfielVormFit | undefined,
  waarden: Record<string, string>,
): ProfielWeergaveParagraaf[] {
  const uit: ProfielWeergaveParagraaf[] = []
  vorm.paragrafen.forEach((paragraaf, index) => {
    const anker = ankerRun(paragraaf)
    if (paragraaf.token) {
      const regels = fit?.regels[paragraaf.token] ?? []
      const schaal = fit?.tokenSchaal ?? 1
      const regelSchaal = fit?.regelSchaal ?? 1
      const basisPt = anker?.pt ?? paragraaf.eindePt ?? 18
      const afstand = regelafstand(paragraaf, basisPt) * regelSchaal
      const glyph = glyphRun(paragraaf)
      const naarRun = (run: ProfielRegelRun): ProfielWeergaveRun => {
        const bron = run.stijl === 'glyph' && glyph ? glyph : anker
        return {
          tekst: run.tekst,
          pt: (bron?.pt ?? basisPt) * schaal,
          vet: run.stijl === 'vet' ? true : (bron?.vet ?? false),
          cursief: bron?.cursief ?? false,
          kleur: bron?.kleur ?? null,
          lettertype: bron?.lettertype ?? null,
          spatiePt: bron?.spatiePt ?? 0,
        }
      }
      regels.forEach((regel, regelIndex) => {
        uit.push({
          sleutel: `${index}-${regelIndex}`,
          token: paragraaf.token,
          inlineToken: null,
          regel,
          runs: regel.runs.map(naarRun),
          regelafstandPt: regel.soort === 'witregel' ? afstand / 2 : afstand,
          voorPt: paragraaf.voorPt,
          naPt: paragraaf.naPt,
          margeLinksPt: paragraaf.margeLinksPt,
          inspringPt: paragraaf.inspringPt,
          uitlijning: paragraaf.uitlijning,
          leegPt: basisPt * schaal,
        })
      })
      if (regels.length === 0) {
        uit.push({
          sleutel: `${index}-leeg`,
          token: paragraaf.token,
          inlineToken: null,
          regel: null,
          runs: [],
          regelafstandPt: afstand,
          voorPt: paragraaf.voorPt,
          naPt: paragraaf.naPt,
          margeLinksPt: paragraaf.margeLinksPt,
          inspringPt: paragraaf.inspringPt,
          uitlijning: paragraaf.uitlijning,
          leegPt: basisPt * schaal,
        })
      }
      return
    }
    const schaal = fit?.paragraafSchaal[index] ?? 1
    const runs: ProfielWeergaveRun[] = paragraaf.runs.map((r) => {
      const heeftToken = r.tekst.includes('{{')
      return {
        tekst: vulInline(r, waarden),
        pt: heeftToken ? r.pt * schaal : r.pt,
        vet: r.vet,
        cursief: r.cursief,
        kleur: r.kleur,
        lettertype: r.lettertype,
        spatiePt: r.spatiePt,
      }
    })
    const pt = Math.max(...runs.map((r) => r.pt), paragraaf.eindePt ?? 0, 1)
    const inline = paragraaf.runs.map((r) => r.tekst).join('').match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/)?.[1] ?? null
    uit.push({
      sleutel: `${index}`,
      token: null,
      inlineToken: inline,
      regel: null,
      runs,
      regelafstandPt: regelafstand(paragraaf, pt) * (fit?.vasteRegelSchaal ?? 1),
      voorPt: paragraaf.voorPt,
      naPt: paragraaf.naPt,
      margeLinksPt: paragraaf.margeLinksPt,
      inspringPt: paragraaf.inspringPt,
      uitlijning: paragraaf.uitlijning,
      leegPt: paragraaf.eindePt ?? pt,
    })
  })
  return uit
}

export type ProfielSlideModelVorm =
  | Exclude<ProfielLayoutVorm, ProfielLayoutTekst>
  | (Omit<ProfielLayoutTekst, 'paragrafen'> & { paragrafen: ProfielWeergaveParagraaf[] })

export interface ProfielSlideWeergaveModel {
  nummer: number
  breedteEmu: number
  hoogteEmu: number
  achtergrond: string | null
  vormen: ProfielSlideModelVorm[]
}

/**
 * Het tekenmodel van één slide voor `ProfielSlide` (@streep/ui): de vormen uit de layout
 * met de tekst ingevuld en de autofit toegepast. Een vorm die in de `.pptx` breder wordt
 * (de naam op de cover) krijgt hier dezelfde breedte; een vorm zonder terugloop tekent in
 * de browser sowieso op zijn eigen breedte.
 */
export function profielSlideModel(
  layout: ProfielLayout,
  slide: ProfielLayoutSlide,
  fit: ProfielFit,
  data: ProfielData,
  opties: { tokensTonen?: boolean } = {},
): ProfielSlideWeergaveModel {
  if (opties.tokensTonen) return profielSlideMetTokens(layout, slide)
  const waarden = profielInlineWaarden(data)
  return {
    nummer: slide.nummer,
    breedteEmu: layout.breedteEmu,
    hoogteEmu: layout.hoogteEmu,
    achtergrond: slide.achtergrond,
    vormen: slide.vormen.map((vorm) => {
      if (vorm.soort !== 'tekst') return vorm
      const vormFit = fit.vormen[profielVormSleutel(slide.nummer, vorm.id)]
      const terugloopt = vormFit?.terugloopBreedteEmu ?? null
      return {
        ...vorm,
        ...(terugloopt !== null ? { terugloop: true, cx: terugloopt } : {}),
        paragrafen: profielWeergaveParagrafen(vorm, vormFit, waarden),
      }
    }),
  }
}

/** De template zelf: de tokens staan er letterlijk, voor de vergelijking vóór activeren. */
function profielSlideMetTokens(layout: ProfielLayout, slide: ProfielLayoutSlide): ProfielSlideWeergaveModel {
  return {
    nummer: slide.nummer,
    breedteEmu: layout.breedteEmu,
    hoogteEmu: layout.hoogteEmu,
    achtergrond: slide.achtergrond,
    vormen: slide.vormen.map((vorm) => {
      if (vorm.soort !== 'tekst') return vorm
      return {
        ...vorm,
        paragrafen: vorm.paragrafen.map((p, index): ProfielWeergaveParagraaf => {
          const pt = Math.max(...p.runs.map((r) => r.pt), p.eindePt ?? 0, 1)
          return {
            sleutel: String(index),
            token: null,
            inlineToken: null,
            regel: null,
            runs: p.runs.map((r) => ({ ...r })),
            regelafstandPt: regelafstand(p, pt),
            voorPt: p.voorPt,
            naPt: p.naPt,
            margeLinksPt: p.margeLinksPt,
            inspringPt: p.inspringPt,
            uitlijning: p.uitlijning,
            leegPt: p.eindePt ?? pt,
          }
        }),
      }
    }),
  }
}

// ── Foto: bijsnijden in plaats van uitrekken ─────────────────────────────────

export interface ProfielBijsnijding {
  l: number
  t: number
  r: number
  b: number
}

/**
 * De `a:srcRect`-fracties die een foto zonder uitrekken in een kader leggen ("cover"),
 * met de gekozen uitsnede. Dezelfde berekening stuurt de schermweergave.
 */
export function berekenProfielBijsnijding(
  fotoBreedte: number,
  fotoHoogte: number,
  kaderBreedte: number,
  kaderHoogte: number,
  uitsnede: ProfielFotoUitsnede | null | undefined,
): ProfielBijsnijding {
  const keuze = uitsnede ?? PROFIEL_FOTO_UITSNEDE_STANDAARD
  if (fotoBreedte <= 0 || fotoHoogte <= 0 || kaderBreedte <= 0 || kaderHoogte <= 0) {
    return { l: 0, t: 0, r: 0, b: 0 }
  }
  const kaderVerhouding = kaderBreedte / kaderHoogte
  const fotoVerhouding = fotoBreedte / fotoHoogte
  let houdB = 1
  let houdH = 1
  if (fotoVerhouding > kaderVerhouding) houdB = kaderVerhouding / fotoVerhouding
  else houdH = fotoVerhouding / kaderVerhouding
  const zoom = Math.max(1, keuze.zoom)
  houdB /= zoom
  houdH /= zoom
  const l = (1 - houdB) * keuze.x
  const t = (1 - houdH) * keuze.y
  return { l, t, r: 1 - houdB - l, b: 1 - houdH - t }
}

// ── Layout-vergelijking (T2) ─────────────────────────────────────────────────

export type ProfielLayoutVerschilSoort = 'ONTBREEKT' | 'EXTRA' | 'VERSCHOVEN' | 'ANDERE_GROOTTE' | 'ANDERE_NAAM'

export interface ProfielLayoutVerschil {
  slide: number
  vormId: number
  naam: string
  soort: ProfielLayoutVerschilSoort
  detail: string
}

/**
 * Vergelijk per slide de vormen op naam, positie en grootte. Goedkoop en zonder render:
 * dit vangt een verschoven of verdwenen vlak in de altijd-draaiende test en in de
 * "vergelijking vóór activeren" in het template-beheer.
 *
 * `negeerNaam`: vormen die bewust anders heten (de `profiel-foto`-markering).
 */
export function vergelijkProfielLayouts(
  referentie: ProfielLayout,
  kandidaat: ProfielLayout,
  opties: { toleranceEmu?: number; negeerNaam?: boolean } = {},
): ProfielLayoutVerschil[] {
  const tolerantie = opties.toleranceEmu ?? 0
  const verschillen: ProfielLayoutVerschil[] = []
  for (const slideA of referentie.slides) {
    const slideB = kandidaat.slides.find((s) => s.nummer === slideA.nummer)
    const vormenB = new Map((slideB?.vormen ?? []).map((v) => [`${v.uitLayout ? 'L' : 'S'}${v.id}`, v]))
    for (const a of slideA.vormen) {
      const sleutel = `${a.uitLayout ? 'L' : 'S'}${a.id}`
      const b = vormenB.get(sleutel)
      if (!b) {
        verschillen.push({ slide: slideA.nummer, vormId: a.id, naam: a.naam, soort: 'ONTBREEKT', detail: `Vorm "${a.naam}" ontbreekt` })
        continue
      }
      vormenB.delete(sleutel)
      if (!opties.negeerNaam && a.naam !== b.naam) {
        verschillen.push({ slide: slideA.nummer, vormId: a.id, naam: a.naam, soort: 'ANDERE_NAAM', detail: `Heet nu "${b.naam}"` })
      }
      if (Math.abs(a.x - b.x) > tolerantie || Math.abs(a.y - b.y) > tolerantie) {
        verschillen.push({
          slide: slideA.nummer,
          vormId: a.id,
          naam: a.naam,
          soort: 'VERSCHOVEN',
          detail: `Van (${a.x}, ${a.y}) naar (${b.x}, ${b.y})`,
        })
      }
      if (Math.abs(a.cx - b.cx) > tolerantie || Math.abs(a.cy - b.cy) > tolerantie) {
        verschillen.push({
          slide: slideA.nummer,
          vormId: a.id,
          naam: a.naam,
          soort: 'ANDERE_GROOTTE',
          detail: `Van ${a.cx}x${a.cy} naar ${b.cx}x${b.cy}`,
        })
      }
    }
    for (const b of vormenB.values()) {
      verschillen.push({ slide: slideA.nummer, vormId: b.id, naam: b.naam, soort: 'EXTRA', detail: `Nieuwe vorm "${b.naam}"` })
    }
  }
  for (const slideB of kandidaat.slides) {
    if (!referentie.slides.some((s) => s.nummer === slideB.nummer)) {
      for (const b of slideB.vormen) {
        verschillen.push({ slide: slideB.nummer, vormId: b.id, naam: b.naam, soort: 'EXTRA', detail: `Slide ${slideB.nummer} is nieuw` })
      }
    }
  }
  return verschillen
}
